// NOA — chatbot determinista 🐢 (decisión de Patricio: sin IA por ahora).
// Responde CALCULANDO sobre los datos vivos (brief/hoteles/alertas/premio)
// con el motor de intenciones de lib/noa.ts. Instantáneo y sin inventar.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Keyboard, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Header from '@/components/Header';
import RoleSheet from '@/components/RoleSheet';
import BotonVoz from '@/components/BotonVoz';
import Tortugona from '@/components/Tortugona';
import { api, type Brief, type IaSeccion, type Premio, type HotelRow, type Source } from '@/api/client';
import { type Alerta } from '@/constants/data';
import { noaResponde, noaSugerencias, type NoaCtx } from '@/lib/noa';
import { useRole } from '@/context/RoleContext';
import { primerNombre } from '@/lib/format';
import { C, F, R } from '@/constants/theme';

interface Msg {
  who: 'me' | 'ia';
  txt: string;
  rows?: Array<[string, string, 'good' | 'bad' | 'flat']>;
  foot?: string;
  typing?: boolean;
  tortugonas?: boolean;   // el podio luce Tortugonas oro/plata/bronce (jamás medallas)
  secciones?: IaSeccion[];
  /* La pregunta que la dejó sin respuesta: es lo que se rellena al enseñarle. */
  noSupe?: string;
}

export default function IaScreen() {
  const insets = useSafeAreaInsets();
  const { sesion } = useRole();
  /* Saluda por su nombre: Noa habla con una persona. Y sin el emoji de tortuga
     —pedido de Patricio— porque la Tortugona de marca ya está justo encima y
     dos tortugas distintas en la misma pantalla no son una marca, son un
     descuido. */
  const yo = primerNombre(sesion?.nombre);
  const [msgs, setMsgs] = useState<Msg[]>([
    { who: 'ia', txt: `${yo ? `¡Hola, ${yo}!` : '¡Hola!'} Soy Noa. Pregúntame por la producción, la ocupación, el ADR, un hotel concreto, las alertas o los Premios Noa.` },
  ]);
  const [input, setInput] = useState('');
  const [roleSheet, setRoleSheet] = useState(false);
  const [source, setSource] = useState<Source | undefined>(undefined);
  const [brief, setBrief] = useState<Brief | null>(null);
  const [hoteles, setHoteles] = useState<HotelRow[]>([]);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [premio, setPremio] = useState<Premio | null>(null);
  const scroll = useRef<ScrollView>(null);
  /* 🔴 ESCRIBIR A CIEGAS EN ANDROID. Patricio, 24-08: «nos despliega el teclado
     pero no vemos lo que estamos escribiendo». Aquí el `behavior` de
     KeyboardAvoidingView era `undefined` en Android, y durante años eso bastó
     porque Android ENCOGÍA la ventana al abrir el teclado. Con el modo a
     pantalla completa que Expo 54 trae de serie ya NO la encoge: el teclado se
     pone ENCIMA, y la caja de texto se queda debajo.
     Se arregla midiendo el teclado y apartando el contenido a mano. Se hace en
     JavaScript a propósito: la alternativa (`softwareKeyboardLayoutMode` en
     app.json) cambia la HUELLA del binario, y con huella nueva las
     actualizaciones por aire dejan de llegar a los móviles ya instalados. Esto
     viaja por aire. */
  const [tecladoAlto, setTecladoAlto] = useState(0);
  useEffect(() => {
    if (Platform.OS !== 'android') return;   // en iOS el `behavior: padding` ya funciona
    const abre = Keyboard.addListener('keyboardDidShow', (e) => {
      /* 🔴 EL HUECO ES EL TECLADO ENTERO, no el teclado menos la barra de
         pestañas. El primer intento restaba esa barra —parecía lo correcto,
         porque ocupa sitio abajo— y el resultado fue el que describió Patricio:
         «si bien sube no sube lo suficiente», justo el alto de esa barra. Ahora
         la barra se aparta al escribir (`tabBarHideOnKeyboard`), la pantalla
         llega hasta abajo y el teclado entero es el hueco que hay que dejar.
         Si algún día esa barra no se apartara, esto dejaría un hueco de más en
         vez de tapar la caja: se ve raro, pero se puede escribir — y ese es el
         lado por el que conviene equivocarse. */
      setTecladoAlto(e.endCoordinates.height);
      setTimeout(() => scroll.current?.scrollToEnd({ animated: true }), 80);
    });
    const cierra = Keyboard.addListener('keyboardDidHide', () => setTecladoAlto(0));
    return () => { abre.remove(); cierra.remove(); };
  }, []);

  useEffect(() => {
    api.brief().then((r) => { setBrief(r.data); setSource(r.source); });
    api.hoteles().then((r) => setHoteles(r.data));
    api.alertas().then((r) => setAlertas(r.data));
    api.premio().then((r) => setPremio(r.data));
  }, []);

  const ctx: NoaCtx = useMemo(() => ({ brief, hoteles, alertas, premio, nombre: yo }), [brief, hoteles, alertas, premio, yo]);
  /* Los nombres de los hoteles, para que el dictado los espere. Se recalcula solo
     cuando cambia la lista, no en cada tecla. */
  const vocVoz = useMemo(() => hoteles.map((h) => h.n).filter(Boolean), [hoteles]);
  const sugerencias = useMemo(() => noaSugerencias(ctx), [ctx]);

  const ask = (q: string) => {
    if (!q.trim()) return;
    setMsgs((m) => [...m, { who: 'me', txt: q }, { who: 'ia', txt: '…', typing: true }]);
    setInput('');
    // pequeño delay de cortesía: se siente conversación (algunas respuestas
    // ahora consultan folios vivos y tardan lo que tarde la red)
    setTimeout(async () => {
      const r = await noaResponde(q, ctx);
      setMsgs((m) => [...m.filter((x) => !x.typing),
        { who: 'ia', txt: r.txt, rows: r.rows, foot: r.foot, tortugonas: r.tortugonas,
          secciones: r.secciones, noSupe: r.noSupe ? q : undefined }]);
      /* El cuaderno: qué se le pregunta y si supo contestar. Sin esto, cada
         pregunta que no entiende se pierde al cerrar el chat y no hay forma de
         saber qué enseñarle. `intencion` es la primera palabra del titular de
         la respuesta, que basta para agrupar. */
      api.noaApunta(q, r.intencion ?? null, !r.noSupe);
      setTimeout(() => scroll.current?.scrollToEnd({ animated: true }), 80);
    }, 550);
  };

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: insets.top }}>
      <Header onAvatar={() => setRoleSheet(true)} source={source} />
      <KeyboardAvoidingView
        style={{ flex: 1, paddingBottom: tecladoAlto }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={8}
      >
        <ScrollView
          ref={scroll}
          contentContainerStyle={{ paddingHorizontal: 18, paddingBottom: 12 }}
          showsVerticalScrollIndicator={false}
          onContentSizeChange={() => scroll.current?.scrollToEnd({ animated: true })}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <Text style={s.title}><Text style={{ color: C.aqua }}>Noa</Text></Text>
            <Tortugona metal="noa" size={30} />
          </View>
          <Text style={s.sub}>La mascota que se sabe los números del grupo — siempre con el dato real de Hotelos</Text>

          <View style={{ gap: 10 }}>
            {msgs.map((msg, i) => (
              <View key={i} style={[s.bub, msg.who === 'me' ? s.bubMe : s.bubIa, msg.secciones?.length ? s.bubAncha : null]}>
                <Text style={[s.bubTxt, msg.who === 'me' && { color: '#04252A', fontFamily: F.uiMed }]}>
                  {msg.typing ? 'Calculando…' : msg.txt}
                </Text>
                {msg.rows?.length ? (
                  <View style={s.mini}>
                    {msg.rows.map(([l, v, k], j) => (
                      <View key={j} style={s.miniRow}>
                        {msg.tortugonas && j < 3 ? (
                          <Tortugona metal={(['oro', 'plata', 'bronce'] as const)[j]} size={20} />
                        ) : null}
                        <Text style={s.miniLbl}>{l}</Text>
                        <Text style={[s.miniVal, { color: k === 'bad' ? C.bad : k === 'good' ? C.good : C.ink }]}>{v}</Text>
                      </View>
                    ))}
                  </View>
                ) : null}
                {msg.secciones?.length ? (
                  <View style={{ marginTop: 12, gap: 12 }}>
                    {msg.secciones.map((sec, j) => (
                      <View key={j} style={s.sec}>
                        <View style={s.secHdr}>
                          <Text style={s.secT}>{sec.t}</Text>
                          {sec.sub ? <Text style={s.secSub}>{sec.sub}</Text> : null}
                        </View>
                        {sec.filas.map(([l, v, kind], k) => (
                          <View key={k} style={s.miniRow}>
                            <Text style={s.miniLbl} numberOfLines={1}>{l}</Text>
                            <Text style={[s.miniVal, kind === 'bad' ? { color: C.bad } : kind === 'good' ? { color: C.good } : null]}>{v}</Text>
                          </View>
                        ))}
                        {sec.total ? (
                          <View style={[s.miniRow, s.secTot]}>
                            <Text style={s.secTotLbl}>{sec.total[0] || 'Subtotal'}</Text>
                            <Text style={s.secTotVal}>{sec.total[1]}</Text>
                          </View>
                        ) : null}
                      </View>
                    ))}
                  </View>
                ) : null}
                {msg.foot ? <Text style={s.bubFoot}>{msg.foot}</Text> : null}
                {/* 🔴 AQUÍ NO SE ENSEÑA. Hubo un botón «enséñame la respuesta»
                    en cada fallo, y Patricio lo cortó con buen criterio: una
                    cosa es improvisar una respuesta en caliente y otra revisar
                    la lista de lo que de verdad se pregunta y no se sabe. Se
                    enseña desde Sistemas › Noa aprende, sobre el cuaderno
                    entero y viendo cuántas veces lo han preguntado. */}
              </View>
            ))}
          </View>

          <View style={s.suggWrap}>
            {sugerencias.map((q) => (
              <Pressable key={q} style={s.sugg} onPress={() => ask(q)}>
                <Text style={s.suggTxt}>{q}</Text>
              </Pressable>
            ))}
          </View>
        </ScrollView>

        <View style={[s.inputRow, { paddingBottom: 10 }]}>
          <TextInput
            style={s.input}
            placeholder="Pregunta a Noa…"
            placeholderTextColor={C.faint}
            value={input}
            onChangeText={setInput}
            onSubmitEditing={() => ask(input)}
            returnKeyType="send"
          />
          {/* 🔴 EL MICRÓFONO NO PREGUNTA SOLO: deja la frase en el campo y la
              ENVÍA. Pedirle al usuario que hable y luego que además toque la
              flecha es cobrarle dos gestos por uno — y en un móvil, hablando, ese
              segundo gesto es justo el que no tienes la mano libre para dar.
              Los parciales sí van al campo mientras habla, para que vea que le
              está oyendo; si se equivoca, el texto está ahí para corregirlo. */}
          <BotonVoz
            onParcial={setInput}
            onTexto={(t) => { setInput(t); ask(t); }}
            vocabulario={vocVoz}
          />
          <Pressable style={s.send} onPress={() => ask(input)}>
            <Text style={{ color: '#04252A', fontFamily: F.uiBold, fontSize: 16 }}>↑</Text>
          </Pressable>
        </View>
      </KeyboardAvoidingView>

      <RoleSheet visible={roleSheet} onClose={() => setRoleSheet(false)} />
    </View>
  );
}

const s = StyleSheet.create({
  title: { fontFamily: F.displayMed, fontSize: 24, color: C.ink },
  sub: { fontFamily: F.ui, fontSize: 12, color: C.muted, marginTop: 3, marginBottom: 14 },
  bub: { maxWidth: '86%', padding: 12, borderRadius: 18 },
  bubAncha: { maxWidth: '98%' },
  sec: { backgroundColor: C.bg, borderColor: C.line, borderWidth: 1, borderRadius: 14, padding: 11, gap: 7 },
  secHdr: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 2 },
  secT: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.aquaSoft, letterSpacing: 0.4, textTransform: 'uppercase', flexShrink: 1 },
  secSub: { fontFamily: F.ui, fontSize: 10, color: C.faint },
  secTot: { borderTopColor: C.line, borderTopWidth: 1, paddingTop: 7, marginTop: 1 },
  secTotLbl: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.muted },
  secTotVal: { fontFamily: F.uiBold, fontSize: 12.5, color: C.ink, fontVariant: ['tabular-nums'] },
  bubMe: { alignSelf: 'flex-end', backgroundColor: C.aqua, borderBottomRightRadius: 6 },
  bubIa: { alignSelf: 'flex-start', backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderBottomLeftRadius: 6 },
  bubTxt: { fontFamily: F.ui, fontSize: 13, color: C.ink, lineHeight: 19 },
  mini: { marginTop: 10, borderTopColor: C.line, borderTopWidth: 1, paddingTop: 10, gap: 8 },
  miniRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
  miniLbl: { fontFamily: F.ui, fontSize: 12, color: C.muted, flexShrink: 1 },
  /* 🔴 EL COLOR VA AQUÍ, no solo en línea. Este estilo se usaba únicamente en
     las filas sueltas, donde el color se aplicaba aparte según el signo; al
     reutilizarlo en las secciones del desglose los importes salían NEGROS
     sobre fondo oscuro — invisibles. Un estilo de texto sin color es una
     trampa esperando a que alguien lo reutilice. */
  miniVal: { fontFamily: F.uiBold, fontSize: 12, color: C.ink, fontVariant: ['tabular-nums'], textAlign: 'right' },
  bubFoot: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 9, lineHeight: 16 },
  suggWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  sugg: { borderColor: 'rgba(30,211,199,0.35)', borderWidth: 1, backgroundColor: 'rgba(30,211,199,0.06)', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 9 },
  suggTxt: { fontFamily: F.ui, fontSize: 12, color: C.aquaSoft },
  inputRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 18, paddingTop: 8, backgroundColor: C.bg },
  input: {
    flex: 1, backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderRadius: R.pill,
    paddingHorizontal: 16, paddingVertical: 12, color: C.ink, fontFamily: F.ui, fontSize: 13,
  },
  send: { width: 44, height: 44, borderRadius: 22, backgroundColor: C.aqua, alignItems: 'center', justifyContent: 'center' },
});
